/**
 * Tela do caixa da casa: o miolo de Finanças da origem, com as quatro abas.
 *
 * Papel: abre com o cabeçalho do módulo, "Finanças" e "CONTA BANCÁRIA C6
 * BANK", e o cabeçalho de seção "Fechamentos e Conciliação Bancária",
 * sublinhado (HTML principal l. 4362–4374); embaixo, o `QuadroEmbutido` de tema
 * `financeiro` com Fechamento, Lançamentos, Cadastros e Outros Bancos, nessa
 * ordem (l. 309–312), abrindo em Fechamento. Guarda o mês aberto, que
 * Fechamento, Lançamentos e Outros Bancos leem.
 *
 * Como usar: o mês aberto começa no mês de hoje, em hora local, e só as setas
 * ‹ › o mudam; ele não vai para o endereço, e recarregar a página volta ao mês
 * de hoje, como na origem. Cada aba lê o que precisa sozinha, e o painel de
 * cada uma fica montado o tempo todo — é o que mantém filtro, seleção e texto
 * digitado de uma aba quando a pessoa vai a outra e volta.
 *
 * O que vier abaixo do cabeçalho de seção deverá ficar depois dele, no mesmo
 * `<div>`: o sublinhado é a borda inferior do `CabecalhoDeSecao`, e um bloco
 * posto entre os dois cabeçalhos quebra a moldura que o HTML principal desenha.
 * No papel do Fechamento os dois cabeçalhos somem e o painel perde o respiro,
 * para a folha começar na borda do papel.
 */
import {
  IconBuildingBank,
  IconFolders,
  IconLayoutDashboard,
  IconListDetails,
} from '@tabler/icons-react';
import { useState } from 'react';

import { type AbaDoApp, QuadroEmbutido } from '@/shared/embutido';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { AbaCadastros } from '../components/aba-cadastros';
import { AbaOutrosBancos } from '../components/aba-outros-bancos';
import { PainelDeFechamento } from '../components/painel-de-fechamento';
import { PainelDeLancamentos } from '../components/painel-de-lancamentos';
import { competenciaDeHoje, somarMeses } from '../formato';

/** As quatro abas, na ordem e com os ícones da origem. */
const ABAS: readonly AbaDoApp[] = [
  { chave: 'fechamento', rotulo: 'Fechamento', Icone: IconLayoutDashboard },
  { chave: 'lancamentos', rotulo: 'Lançamentos', Icone: IconListDetails },
  { chave: 'cadastros', rotulo: 'Cadastros', Icone: IconFolders },
  { chave: 'outrosbancos', rotulo: 'Outros Bancos', Icone: IconBuildingBank },
];

/**
 * Página de `/financas`.
 *
 * Quem a monta é o `router.tsx`, dentro da rota protegida.
 *
 * @returns Os dois cabeçalhos e o miolo com as quatro abas.
 */
export function PaginaFinancas() {
  const [mesAberto, setMesAberto] = useState(competenciaDeHoje);

  /**
   * Anda o mês aberto, como o `navigate` da origem.
   *
   * @param meses - -1 volta um mês, +1 avança um.
   */
  function handleMudarMes(meses: number) {
    setMesAberto((atual) => somarMeses(atual, meses));
  }

  return (
    <div>
      <div className="print:[body[data-impressao=imprimindo-fechamento]_&]:hidden">
        <CabecalhoDoModulo titulo="Finanças" subtitulo="CONTA BANCÁRIA C6 BANK" />
        <CabecalhoDeSecao
          titulo="Fechamentos e Conciliação Bancária"
          className="border-b border-linha-forte pb-4"
        />
      </div>
      <QuadroEmbutido
        tema="financeiro"
        abas={ABAS}
        className="print:[body[data-impressao=imprimindo-fechamento]_&_[role=tabpanel]]:p-0"
        paineis={{
          fechamento: (
            <PainelDeFechamento competencia={mesAberto} onMudarMes={handleMudarMes} />
          ),
          lancamentos: <PainelDeLancamentos mesAberto={mesAberto} />,
          cadastros: <AbaCadastros />,
          outrosbancos: (
            <AbaOutrosBancos competencia={mesAberto} onMudarMes={handleMudarMes} />
          ),
        }}
      />
    </div>
  );
}
